<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import {
  isFieldCriterionActive,
  matchesFieldFilter,
} from 'dashboard/helper/funnelHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FunnelStageColumn from 'dashboard/components-next/funnels/FunnelStageColumn.vue';

const props = defineProps({
  funnel: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const cardsGetter = useMapGetter('funnels/getCards');
const archivedCardsGetter = useMapGetter('funnels/getArchivedCards');

// Archived view: the same stage columns, filled from the archived list instead
// of the board. Never both at once — an archived card back among the active
// ones would defeat the point of archiving it.
const showArchived = ref(false);

// Local, drag-mutable copy of the board derived from the store. Rebuilt on
// funnel change, when store cards change, and on rollback.
const columns = ref([]);

// Client-side filters over the already-loaded cards (no API call). Both may be
// active at once — a card must pass every active filter (intersection).
//   searchQuery      matches on the contact name
//   field filter     matches on a chosen custom field value, per its type
const searchQuery = ref('');
const filterFieldKey = ref('');
const filterCriterion = ref({});

const funnelFields = computed(() =>
  [...(props.funnel.fields || [])].sort((a, b) => a.position - b.position)
);

const fieldOptions = computed(() => [
  { value: '', label: t('FUNNELS.FILTER.FIELD_ALL') },
  ...funnelFields.value.map(field => ({ value: field.key, label: field.name })),
]);

const selectedField = computed(
  () =>
    funnelFields.value.find(field => field.key === filterFieldKey.value) || null
);

const listOptions = computed(() =>
  (selectedField.value?.options || []).map(option => ({
    value: option,
    label: option,
  }))
);

// Reset the criterion whenever the chosen field changes so stale inputs from a
// previous field type never linger.
watch(filterFieldKey, () => {
  filterCriterion.value = {};
});

const fieldFilterActive = computed(
  () =>
    Boolean(selectedField.value) &&
    isFieldCriterionActive(
      selectedField.value.field_type,
      filterCriterion.value
    )
);

const isFiltering = computed(
  () => Boolean(searchQuery.value.trim()) || fieldFilterActive.value
);

const visibleColumns = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const field = selectedField.value;
  const criterion = filterCriterion.value;

  if (!query && !field) return columns.value;

  return columns.value.map(column => ({
    ...column,
    cards: column.cards.filter(card => {
      const nameOk =
        !query || (card.contact?.name || '').toLowerCase().includes(query);
      const fieldOk =
        !field ||
        matchesFieldFilter(
          field.field_type,
          criterion,
          (card.custom_field_values || {})[field.key]
        );
      return nameOk && fieldOk;
    }),
  }));
});

const totalCount = computed(() =>
  columns.value.reduce((sum, column) => sum + column.cards.length, 0)
);

const visibleCount = computed(() =>
  visibleColumns.value.reduce((sum, column) => sum + column.cards.length, 0)
);

const clearFilters = () => {
  searchQuery.value = '';
  filterFieldKey.value = '';
  filterCriterion.value = {};
};

const fetchCards = () => {
  if (props.funnel.id) {
    store.dispatch('funnels/fetchCards', { funnelId: props.funnel.id });
  }
};

const fetchArchivedCards = () => {
  if (props.funnel.id) {
    store.dispatch('funnels/fetchArchivedCards', { funnelId: props.funnel.id });
  }
};

// Refetched on every switch into the archived view rather than kept live: no
// event feeds the archived list, so a stale snapshot is the alternative.
const toggleArchived = () => {
  showArchived.value = !showArchived.value;
  clearFilters();
  if (showArchived.value) fetchArchivedCards();
};

const buildColumns = () => {
  const stages = [...(props.funnel.stages || [])].sort(
    (a, b) => a.position - b.position
  );
  const cards = showArchived.value
    ? archivedCardsGetter.value(props.funnel.id)
    : cardsGetter.value(props.funnel.id);

  // Compare by numeric value: stage.id and card.funnel_stage_id both come from
  // the API as integers, but coercing guards against any serialization drift.
  columns.value = stages.map(stage => ({
    stage,
    cards: cards
      .filter(card => Number(card.funnel_stage_id) === Number(stage.id))
      .sort((a, b) => a.position - b.position)
      .map(card => ({ ...card })),
  }));
};

watch(() => props.funnel.id, fetchCards, { immediate: true });

watch(
  () => [
    props.funnel.id,
    showArchived.value,
    cardsGetter.value(props.funnel.id),
    archivedCardsGetter.value(props.funnel.id),
  ],
  () => buildColumns(),
  { immediate: true }
);

// Turn the structured 422 (§9.6) into a readable move error naming the missing
// required field(s); fall back to the generic message for any other failure.
const moveErrorMessage = error => {
  const errors = error?.response?.data?.errors;
  const missing = Array.isArray(errors)
    ? errors.filter(item => item.code === 'missing_required')
    : [];
  if (!missing.length) return t('FUNNELS.CARD.MOVE_ERROR');

  const fields = missing.map(item => `"${item.name}"`).join(', ');
  return t('FUNNELS.CARD.MOVE_MISSING_REQUIRED', { fields }, missing.length);
};

const onCardMove = async ({ stageId, cardId, position }) => {
  try {
    await store.dispatch('funnels/moveCard', {
      funnelId: props.funnel.id,
      cardId,
      funnelStageId: stageId,
      position,
    });
  } catch (error) {
    // The store already rolled the optimistic move back; rebuild the local board
    // from the restored store state and explain why the card returned.
    useAlert(moveErrorMessage(error));
    buildColumns();
  }
};
</script>

<template>
  <div class="flex flex-col h-full gap-4">
    <div class="flex flex-wrap items-center gap-2">
      <div class="w-64 shrink-0">
        <Input
          v-model="searchQuery"
          size="sm"
          :placeholder="t('FUNNELS.SEARCH_PLACEHOLDER')"
        />
      </div>

      <template v-if="funnelFields.length">
        <Select
          v-model="filterFieldKey"
          :options="fieldOptions"
          class="shrink-0"
        />

        <template v-if="selectedField">
          <Input
            v-if="selectedField.field_type === 'text'"
            v-model="filterCriterion.query"
            size="sm"
            class="w-48"
            :placeholder="t('FUNNELS.FILTER.TEXT_PLACEHOLDER')"
          />

          <template v-else-if="selectedField.field_type === 'number'">
            <Input
              v-model="filterCriterion.min"
              type="number"
              size="sm"
              class="w-28"
              :placeholder="t('FUNNELS.FILTER.NUMBER_MIN')"
            />
            <Input
              v-model="filterCriterion.max"
              type="number"
              size="sm"
              class="w-28"
              :placeholder="t('FUNNELS.FILTER.NUMBER_MAX')"
            />
          </template>

          <template v-else-if="selectedField.field_type === 'date'">
            <Input
              v-model="filterCriterion.from"
              type="date"
              size="sm"
              class="w-40"
              :placeholder="t('FUNNELS.FILTER.DATE_FROM')"
            />
            <Input
              v-model="filterCriterion.to"
              type="date"
              size="sm"
              class="w-40"
              :placeholder="t('FUNNELS.FILTER.DATE_TO')"
            />
          </template>

          <Select
            v-else-if="selectedField.field_type === 'list'"
            v-model="filterCriterion.option"
            :options="listOptions"
            :placeholder="t('FUNNELS.FILTER.LIST_PLACEHOLDER')"
            class="shrink-0"
          />
        </template>
      </template>

      <template v-if="isFiltering">
        <span class="text-sm text-n-slate-11">
          {{
            t('FUNNELS.FILTER.COUNT', {
              visible: visibleCount,
              total: totalCount,
            })
          }}
        </span>
        <NextButton
          slate
          ghost
          sm
          :label="t('FUNNELS.FILTER.CLEAR')"
          @click="clearFilters"
        />
      </template>

      <NextButton
        slate
        ghost
        sm
        class="ml-auto shrink-0"
        :icon="showArchived ? 'i-lucide-kanban' : 'i-lucide-archive'"
        :label="
          showArchived ? t('FUNNELS.ARCHIVED.HIDE') : t('FUNNELS.ARCHIVED.SHOW')
        "
        @click="toggleArchived"
      />
    </div>

    <p v-if="showArchived" class="text-sm text-n-slate-11">
      {{ t('FUNNELS.ARCHIVED.HINT') }}
    </p>

    <div class="flex flex-1 min-h-0 gap-4 overflow-x-auto">
      <FunnelStageColumn
        v-for="column in visibleColumns"
        :key="column.stage.id"
        :stage="column.stage"
        :cards="column.cards"
        :archived="showArchived"
        @card-move="onCardMove"
      />
    </div>
  </div>
</template>
